import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import SettingsDrawer from '@/features/settings-drawer/SettingsDrawer'
import useAuth from '@/hooks/useAuth'
import useTerminology from '@/hooks/useTerminology'
import { navSections, settingsItems, topLevelItems } from '@/navConfig'
import { CreateModal } from '../app-top-bar/CreateModal'
import {
    canAccessNavEntry,
    canAccessNavSection,
    getVisibleNavItems
} from '../nav-access'

function DrawerSection({ section, navAccess, onNavigate, onCreateClick }) {
    const { t } = useTerminology()
    const [open, setOpen] = useState(false)
    const visibleItems = getVisibleNavItems(section.items, navAccess)

    return (
        <>
            <ListItemButton
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={`mobile-section-${section.id}`}
            >
                <ListItemIcon>{section.icon}</ListItemIcon>
                <ListItemText primary={section.label} />
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={open} unmountOnExit={false}>
                <List id={`mobile-section-${section.id}`} disablePadding>
                    {visibleItems.map((item) => (
                        <ListItemButton
                            key={item.id}
                            component={Link}
                            to={item.path}
                            sx={{ pl: 4 }}
                            onClick={onNavigate}
                        >
                            <ListItemIcon>
                                {item.icon ?? section.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    item.termKey ? t(item.termKey) : item.label
                                }
                            />
                        </ListItemButton>
                    ))}
                    {section.createModal && navAccess.isAuthenticated && (
                        <ListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => onCreateClick(section)}
                        >
                            <ListItemText primary="Create" />
                        </ListItemButton>
                    )}
                </List>
            </Collapse>
        </>
    )
}

DrawerSection.propTypes = {
    section: PropTypes.object.isRequired,
    navAccess: PropTypes.shape({
        isAuthenticated: PropTypes.bool.isRequired,
        isStaff: PropTypes.bool.isRequired,
        user: PropTypes.object
    }).isRequired,
    onNavigate: PropTypes.func.isRequired,
    onCreateClick: PropTypes.func.isRequired
}

export function MobileMoreDrawer({ open, onClose }) {
    const { isAuthenticated, user, activeOrg } = useAuth()
    const isStaff = user?.isStaff === true
    const navAccess = { isAuthenticated, isStaff, user, activeOrg }
    const visiblePinnedSections = navSections.filter(
        (section) => section.pinToTop && canAccessNavSection(section, navAccess)
    )
    const visibleStandardSections = navSections.filter(
        (section) =>
            !section.pinToTop && canAccessNavSection(section, navAccess)
    )
    const visibleSettingsItems = settingsItems.filter((item) =>
        canAccessNavEntry(item, navAccess)
    )
    const [settingsExpanded, setSettingsExpanded] = useState(false)
    const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false)
    const [createModalPrefix, setCreateModalPrefix] = useState(null)

    const handleNavigate = () => onClose()

    return (
        <>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        // Full-height sheet: keep the header clear of the
                        // notch and the last item clear of the home indicator.
                        pt: 'env(safe-area-inset-top, 0px)',
                        pb: 'env(safe-area-inset-bottom, 0px)',
                        pl: 'env(safe-area-inset-left, 0px)',
                        pr: 'env(safe-area-inset-right, 0px)'
                    }
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1
                    }}
                >
                    <Typography variant="h6">Navigation</Typography>
                    <IconButton
                        onClick={onClose}
                        aria-label="Close navigation drawer"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                {/* Nav content */}
                <Box
                    component="nav"
                    aria-label="Mobile navigation"
                    sx={{ flex: 1, overflowY: 'auto' }}
                >
                    <List>
                        {visiblePinnedSections.map((section) => (
                            <DrawerSection
                                key={section.id}
                                section={section}
                                navAccess={navAccess}
                                onNavigate={handleNavigate}
                                onCreateClick={(s) => {
                                    onClose()
                                    setCreateModalPrefix(s.createPathPrefix)
                                }}
                            />
                        ))}

                        {visiblePinnedSections.length > 0 && (
                            <Divider sx={{ my: 0.5 }} />
                        )}

                        {/* Top-level items */}
                        {topLevelItems
                            .filter((item) =>
                                canAccessNavEntry(item, navAccess)
                            )
                            .map((item) => (
                                <ListItemButton
                                    key={item.id}
                                    component={Link}
                                    to={item.path}
                                    onClick={handleNavigate}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.label} />
                                </ListItemButton>
                            ))}

                        <Divider sx={{ my: 0.5 }} />

                        {/* Collapsible sections */}
                        {visibleStandardSections.map((section) => (
                            <DrawerSection
                                key={section.id}
                                section={section}
                                navAccess={navAccess}
                                onNavigate={handleNavigate}
                                onCreateClick={(s) => {
                                    onClose()
                                    setCreateModalPrefix(s.createPathPrefix)
                                }}
                            />
                        ))}

                        <Divider sx={{ my: 0.5 }} />

                        {/* Settings */}
                        <ListItemButton
                            onClick={() => setSettingsExpanded((p) => !p)}
                            aria-expanded={settingsExpanded}
                        >
                            <ListItemIcon>
                                <SettingsOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Settings" />
                            {settingsExpanded ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )}
                        </ListItemButton>
                        <Collapse in={settingsExpanded} unmountOnExit={false}>
                            <List disablePadding>
                                {visibleSettingsItems.map((item) => (
                                    <ListItemButton
                                        key={item.id}
                                        component={Link}
                                        to={item.path}
                                        sx={{ pl: 4 }}
                                        onClick={handleNavigate}
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} />
                                    </ListItemButton>
                                ))}
                                <ListItemButton
                                    sx={{ pl: 4 }}
                                    onClick={() => {
                                        onClose()
                                        setSettingsDrawerOpen(true)
                                    }}
                                >
                                    <ListItemIcon>
                                        <TuneIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Display" />
                                </ListItemButton>
                            </List>
                        </Collapse>
                    </List>
                </Box>
            </Drawer>
            <SettingsDrawer
                open={settingsDrawerOpen}
                onClose={() => setSettingsDrawerOpen(false)}
            />
            <CreateModal
                open={!!createModalPrefix}
                onClose={() => setCreateModalPrefix(null)}
                pathPrefix={createModalPrefix}
            />
        </>
    )
}

MobileMoreDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}
